// src/utils/subPayTerms.js
// ===========================================================================
// Subcontractor pay TERMS on a job: paid by the DAY or by the PROJECT.
//
// WHY: Edwin is one subcontractor but often brings a crew. Instead of adding
// every worker as a sub (or adding "Edwin" to techAssign three times), the
// job carries one term for Edwin:
//
//   Daily    workers x rate per worker per day x days on the job
//            (3 workers x $350 x 2 days = $2,100)
//   Project  one price for the whole job, including every Continue
//
// WHERE IT LIVES (job document)
//   jobs/{id}.subPayTerms = {
//     edwin_garcia: {
//       name            "Edwin Garcia"   (the techAssign name)
//       mode            "daily" | "project"
//       workers         3
//       ratePerWorker   350
//       days            ""  = count the job's own days (start - end)
//                       2   = a typed number of days (this job only)
//       projectAmount   4500
//       setOnJobId      the job the term was typed on ("" = new job)
//       projectJobId    the job that holds the project price
//       computedSig / computedJobId / computedAmount / computedDays
//                       what was last written to the owed line
//       updatedAt / updatedBy
//     }
//   }
//
// WHAT IT WRITES - the SAME owed line every other page already reads:
//   jobs/{id}.subcontractorPay[] = { subcontractorName, basePay, changeOrders }
// so Contractor Pay, Job Estimate Spreadsheet, ClockTime and the dashboards
// tally it with no change. Payments stay in "subcontractorPay" (payments).
//
// RULES
//   - A line is only rewritten when the term or the job's dates change
//     (computedSig). An amount edited on the back end (Contractor Pay /
//     Job Estimate Spreadsheet) is left alone until the term is changed.
//   - Continue copies the term. Daily keeps the crew and rate and counts the
//     new job's own days. Project is owed ONCE - on the job it was set on -
//     so a continued job owes $0 for it (the price "rolls over").
//   - Recurring jobs are not handled here (use Sub Pay per visit).
//
// IMPORTANT: defaultcodebase/subPayTerms.js holds a copy of the pure part of
// this file for the Cloud Function. Keep the two identical.
// ===========================================================================

// ---- pure core (copied to defaultcodebase/subPayTerms.js) ----------------
export const DEFAULT_DAILY_RATE = 350;

export const normalizeSubName = (value = "") =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const termKey = (name = "") => normalizeSubName(name).replace(/ /g, "_");

const num = (v) => {
  const n = Number(String(v ?? "").replace(/[$,]/g, ""));
  return Number.isFinite(n) ? n : 0;
};
export const round2 = (v) => Math.round(num(v) * 100) / 100;

const dayKeyOf = (value) => {
  if (!value) return "";
  if (typeof value === "string") return /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : "";
  if (typeof value.toDate === "function") return value.toDate().toISOString().slice(0, 10);
  if (typeof value === "object" && value.seconds) return new Date(value.seconds * 1000).toISOString().slice(0, 10);
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return "";
};

export const isRecurringJob = (job = {}) => {
  const type =
    typeof job.recurrence === "object" && job.recurrence
      ? job.recurrence.type
      : job.recurrence || job.reccurance || job.recurring || job.repeat;
  return Boolean(type && String(type).toLowerCase().trim() !== "none");
};

export const isContinuationJob = (job = {}) =>
  Boolean(
    job.continuedFromJobId ||
      job.projectContinuedFromJobId ||
      job.previousJobId ||
      job.createdByContinue ||
      job.createdByProjectContinue
  );

/** Days the job runs, start to end, counting both ends. 0 when unscheduled. */
export const jobDayCount = (job = {}) => {
  const start = dayKeyOf(job.startDate);
  if (!start) return 0;
  const end = dayKeyOf(job.endDate) || start;
  const a = Date.UTC(+start.slice(0, 4), +start.slice(5, 7) - 1, +start.slice(8, 10));
  const b = Date.UTC(+end.slice(0, 4), +end.slice(5, 7) - 1, +end.slice(8, 10));
  return Math.max(1, Math.round((b - a) / 86400000) + 1);
};

/** Every calendar day the job runs, start to end (YYYY-MM-DD). [] when unscheduled. */
export const jobDayKeys = (job = {}) => {
  const start = dayKeyOf(job.startDate);
  if (!start) return [];
  const count = Math.min(jobDayCount(job), 366);
  const a = Date.UTC(+start.slice(0, 4), +start.slice(5, 7) - 1, +start.slice(8, 10));
  const out = [];
  for (let i = 0; i < count; i += 1) out.push(new Date(a + i * 86400000).toISOString().slice(0, 10));
  return out;
};

const CLOSED_JOB_STATUSES = new Set(["deleted", "cancelled", "canceled", "void", "voided"]);
export const isClosedJob = (job = {}) => CLOSED_JOB_STATUSES.has(String(job.status || "").toLowerCase().trim());

/**
 * SAME-DAY SPLIT (Daily pay only)
 * A sub paid $350/day who works 5 jobs on one day is owed $350 for that day,
 * not $1,750. planDailySplit() stores on each daily term which days it shares:
 *   term.dayShares = { "2026-10-20": { n: 5, i: 0 } }
 *     n = how many of the sub's daily jobs run that day
 *     i = this job's place in that list (0 gets any leftover penny)
 * Days missing from the map are paid in full. Project pay is never split.
 * dayShares is left OUT of termSig on purpose, so older copies of this file
 * (Cloud Function) don't see it as a change and fight over the line.
 */
const dayShareOf = (term, day) => {
  const s = term && term.dayShares && term.dayShares[day];
  if (!s) return { n: 1, i: 0 };
  const n = Math.max(1, Math.round(num(typeof s === "object" ? s.n : s)) || 1);
  const i = Math.max(0, Math.round(num(typeof s === "object" ? s.i : 0)));
  return { n, i };
};

/**
 * True when this job is where the term was set. A term copied by Continue
 * belongs to the job it came from: its typed day count does not carry, and a
 * project price is not owed again.
 */
export const isOwnTerm = (job = {}, term = {}) =>
  term.setOnJobId ? term.setOnJobId === (job.id || "") : !isContinuationJob(job);

/** What one term owes on this job. */
export const computeTermOwed = (job = {}, term = {}) => {
  const mode = term.mode === "project" ? "project" : "daily";
  const own = isOwnTerm(job, term);
  const autoDays = jobDayCount(job);
  const workers = Math.max(0, Math.round(num(term.workers === "" || term.workers === undefined ? 1 : term.workers)));
  const rate = round2(term.ratePerWorker === "" || term.ratePerWorker === undefined ? DEFAULT_DAILY_RATE : term.ratePerWorker);
  const typedDays = num(term.days);
  const days = own && typedDays > 0 ? typedDays : autoDays;

  if (isRecurringJob(job)) {
    return { mode, own, amount: 0, days, autoDays, workers, rate, skipped: "recurring" };
  }
  if (mode === "project") {
    return {
      mode,
      own,
      amount: own ? round2(term.projectAmount) : 0,
      days,
      autoDays,
      workers,
      rate,
      projectAmount: round2(term.projectAmount),
    };
  }
  // Daily: each calendar day is one day's pay, split with the sub's other
  // daily jobs that same day (see dayShares above). Pennies are spread so the
  // split adds back up to exactly one day.
  const dayCents = Math.round(workers * rate * 100);
  const keys = jobDayKeys(job);
  if (!keys.length || !dayCents) {
    return { mode, own, amount: round2(workers * rate * days), days, autoDays, workers, rate, paidDays: days, splitDays: 0, maxShared: 1 };
  }
  let cents = 0;
  let splitDays = 0;
  let maxShared = 1;
  keys.forEach((d) => {
    const { n, i } = dayShareOf(term, d);
    if (n > 1) splitDays += 1;
    if (n > maxShared) maxShared = n;
    const base = Math.floor(dayCents / n);
    const rem = dayCents - base * n;
    cents += base + (i < rem ? 1 : 0);
  });
  // Typed days (this job only) keep the same average split per day.
  const amount = days === keys.length ? cents / 100 : round2(((cents / keys.length) * days) / 100);
  const paidDays = Math.round((cents / dayCents) * (days / keys.length) * 100) / 100;
  return { mode, own, amount, days, autoDays, workers, rate, paidDays, splitDays, maxShared };
};

export const termSig = (job = {}, term = {}) => {
  const c = computeTermOwed(job, term);
  return JSON.stringify([c.mode, c.own, c.workers, c.rate, c.days, c.mode === "project" ? c.projectAmount : 0, c.skipped || ""]);
};

/** The term has changed (or the job's dates moved) since its line was written. */
export const isTermStale = (job = {}, term = {}) =>
  term.computedSig !== termSig(job, term) ||
  Boolean(term.computedJobId && job.id && term.computedJobId !== job.id);

const defaultSameSub = (a, b) => {
  const x = normalizeSubName(a);
  return Boolean(x) && x === normalizeSubName(b);
};

/**
 * Bring the owed lines in line with the terms.
 *   sameSub(a, b)  true when two names are the same contractor (pass the
 *                  identity matcher so "Edwin Garcia" finds a line saved as
 *                  "Garcia Construction LLC")
 *   force          rewrite even when nothing changed
 *   freshCopy      the job was just copied by Continue: drop the copied lines
 *                  of every sub that has a term (they belong to the old job)
 * Returns { changed, subcontractorPay, subPayTerms }. Never touches lines of
 * subs that have no term.
 */
export const applySubPayTerms = (job = {}, { sameSub = defaultSameSub, force = false, freshCopy = false } = {}) => {
  const terms = job.subPayTerms && typeof job.subPayTerms === "object" ? job.subPayTerms : {};
  // Recurring jobs keep their per-visit Sub Pay; terms never touch them.
  if (isRecurringJob(job)) {
    return { changed: false, subcontractorPay: job.subcontractorPay || [], subPayTerms: terms };
  }
  let lines = (Array.isArray(job.subcontractorPay) ? job.subcontractorPay : []).map((l) => ({ ...l }));
  const nextTerms = { ...terms };
  const jobId = job.id || "";
  let changed = false;

  const findLine = (name) => lines.findIndex((l) => sameSub(l?.subcontractorName || l?.name || "", name));

  Object.entries(terms).forEach(([key, term]) => {
    if (!term || !term.name) return;

    if (freshCopy) {
      let idx = findLine(term.name);
      while (idx >= 0) {
        lines.splice(idx, 1);
        idx = findLine(term.name);
        changed = true;
      }
    }

    const own = isOwnTerm(job, term);
    const ids = {};
    if (jobId && own && !term.setOnJobId) ids.setOnJobId = jobId;
    if (jobId && own && term.mode === "project" && term.projectJobId !== jobId) ids.projectJobId = jobId;

    if (!force && !freshCopy && !isTermStale(job, term)) {
      if (Object.keys(ids).length) {
        nextTerms[key] = { ...term, ...ids };
        changed = true;
      }
      return;
    }

    const calc = computeTermOwed(job, term);
    const idx = findLine(term.name);
    if (calc.amount > 0) {
      if (idx >= 0) {
        lines[idx] = {
          ...lines[idx],
          subcontractorName: lines[idx].subcontractorName || term.name,
          basePay: calc.amount,
          changeOrders: Array.isArray(lines[idx].changeOrders) ? lines[idx].changeOrders : [],
        };
      } else {
        lines.push({ subcontractorName: term.name, basePay: calc.amount, changeOrders: [] });
      }
    } else if (idx >= 0) {
      const cos = Array.isArray(lines[idx].changeOrders) ? lines[idx].changeOrders : [];
      if (cos.length) lines[idx] = { ...lines[idx], basePay: 0 };
      else lines.splice(idx, 1);
    }

    nextTerms[key] = {
      ...term,
      ...ids,
      computedSig: termSig({ ...job }, { ...term, ...ids }),
      computedJobId: jobId,
      computedAmount: calc.amount,
      computedDays: calc.days,
    };
    changed = true;
  });

  return { changed, subcontractorPay: lines, subPayTerms: nextTerms };
};

const owedOnLine = (line) =>
  line
    ? round2(num(line.basePay) + (Array.isArray(line.changeOrders) ? line.changeOrders : []).reduce((s, co) => s + num(co && co.amount), 0))
    : 0;

const sharesSig = (shares) =>
  JSON.stringify(
    Object.keys(shares || {})
      .sort()
      .map((d) => {
        const s = shares[d];
        const n = Math.round(num(typeof s === "object" && s ? s.n : s)) || 1;
        const i = Math.round(num(typeof s === "object" && s ? s.i : 0));
        return n > 1 ? [d, n, i] : null;
      })
      .filter(Boolean)
  );

/**
 * Work out the same-day split for Daily pay across many jobs.
 *   jobs       job docs ({ id, ...data }) - pass every job the sub(s) might be on
 *   groupOf    name -> person key, so "Edwin" and "Edwin Garcia LLC" count as one
 *              (defaults to the plain normalized name)
 *   onlyGroups optional Set of person keys to limit the work to
 * Returns { rows, patches }:
 *   rows    [{ jobId, wo, start, name, before, after, splitDays, maxShared, handEdited }]
 *           one per job + sub that changes
 *   patches { [jobId]: { subPayTerms, subcontractorPay } } to write
 * Nothing is written here. A line edited by hand (basePay no longer equals what
 * the term last wrote) keeps its amount; only its dayShares are updated.
 */
export const planDailySplit = (jobs = [], { groupOf = normalizeSubName, onlyGroups = null } = {}) => {
  const entries = [];
  jobs.forEach((job) => {
    if (!job || !job.id || isRecurringJob(job) || isClosedJob(job)) return;
    Object.entries(job.subPayTerms || {}).forEach(([key, term]) => {
      if (!term || !term.name || term.mode === "project") return;
      const g = groupOf(term.name);
      if (!g || (onlyGroups && !onlyGroups.has(g))) return;
      entries.push({ job, key, term, g, days: jobDayKeys(job) });
    });
  });

  // person + day -> the daily jobs that day, in a fixed order
  const byDay = {};
  entries.forEach((e) =>
    e.days.forEach((d) => {
      const k = `${e.g}|${d}`;
      (byDay[k] = byDay[k] || []).push(e);
    })
  );
  const order = (e) => `${dayKeyOf(e.job.startDate)}|${String(e.job.wo || "").padStart(12, "0")}|${e.job.id}`;
  Object.values(byDay).forEach((list) => list.sort((a, b) => order(a).localeCompare(order(b))));

  const sameSub = (a, b) => {
    const x = groupOf(a);
    return Boolean(x) && x === groupOf(b);
  };
  const working = {}; // jobId -> job with patches applied so far
  const rows = [];

  entries.forEach((e) => {
    const shares = {};
    e.days.forEach((d) => {
      const list = byDay[`${e.g}|${d}`];
      if (list.length > 1) shares[d] = { n: list.length, i: list.indexOf(e) };
    });
    const cur = working[e.job.id] || e.job;
    const term = (cur.subPayTerms || {})[e.key] || e.term;
    const lines = Array.isArray(cur.subcontractorPay) ? cur.subcontractorPay : [];
    const line = lines.find((l) => sameSub((l && (l.subcontractorName || l.name)) || "", term.name)) || null;
    const nextTerm = { ...term, dayShares: shares };
    const calc = computeTermOwed(cur, nextTerm);
    const wroteBefore = term.computedSig !== undefined && term.computedSig !== "" && term.computedAmount !== undefined;
    const handEdited = Boolean(line && wroteBefore && round2(line.basePay) !== round2(term.computedAmount));
    const sharesChanged = sharesSig(term.dayShares) !== sharesSig(shares);
    const lineWrong = !handEdited && round2(line ? line.basePay : 0) !== round2(calc.amount);
    if (!sharesChanged && !lineWrong) return;

    let next;
    if (handEdited) {
      next = { ...cur, subPayTerms: { ...(cur.subPayTerms || {}), [e.key]: nextTerm } };
    } else {
      const r = applySubPayTerms({ ...cur, subPayTerms: { [e.key]: nextTerm } }, { sameSub, force: true });
      next = {
        ...cur,
        subcontractorPay: r.subcontractorPay,
        subPayTerms: { ...(cur.subPayTerms || {}), [e.key]: r.subPayTerms[e.key] },
      };
    }
    working[e.job.id] = next;
    const after = (next.subcontractorPay || []).find((l) => sameSub((l && (l.subcontractorName || l.name)) || "", term.name));
    rows.push({
      jobId: e.job.id,
      wo: e.job.wo || "",
      start: dayKeyOf(e.job.startDate),
      name: term.name,
      before: owedOnLine(line),
      after: owedOnLine(after),
      splitDays: calc.splitDays,
      maxShared: calc.maxShared,
      handEdited,
    });
  });

  const patches = {};
  Object.entries(working).forEach(([id, j]) => {
    patches[id] = { subPayTerms: j.subPayTerms || {}, subcontractorPay: j.subcontractorPay || [] };
  });
  return { rows, patches };
};
// ---- end pure core ---------------------------------------------------------

/** Money owed on one stored line (same as Contractor Pay's lineOwed). */
export const lineOwed = (line = {}) =>
  round2(
    num(line.basePay) +
      (Array.isArray(line.changeOrders) ? line.changeOrders : []).reduce((s, co) => s + num(co?.amount), 0)
  );

/** Short words for a term: "3 workers × $350/day × 2 days" or "Project $4,500". */
export const describeTerm = (job, term, money = (v) => `$${round2(v).toFixed(2)}`) => {
  const c = computeTermOwed(job, term);
  if (c.skipped === "recurring") return "Recurring job - use Sub Pay per visit";
  if (c.mode === "project") {
    return c.own ? `Project price ${money(c.projectAmount)}` : `Project price ${money(c.projectAmount)} (owed on the first job)`;
  }
  const base = `${c.workers} worker${c.workers === 1 ? "" : "s"} × ${money(c.rate)}/day × ${c.days} day${c.days === 1 ? "" : "s"}`;
  return c.splitDays ? `${base} · ${splitNote(c)} = ${money(c.amount)}` : base;
};

/** "split with 4 other jobs on 1 day" - the same-day split in words. */
export const splitNote = (c) =>
  `split with ${c.maxShared - 1} other job${c.maxShared - 1 === 1 ? "" : "s"} on ${c.splitDays} day${c.splitDays === 1 ? "" : "s"}`;

/**
 * A fresh term for a sub picked on a job. Pass jobId when the job already
 * exists: a term typed on a job belongs to that job (even a continued one),
 * so its price / days apply there.
 */
export const newTerm = (name, { mode = "daily", rate = DEFAULT_DAILY_RATE, jobId = "" } = {}) => ({
  name,
  mode,
  workers: 1,
  ratePerWorker: rate,
  days: "",
  projectAmount: "",
  setOnJobId: jobId || "",
  projectJobId: mode === "project" ? jobId || "" : "",
});

/** Roles that can see and edit sub pay on jobs. Change here to widen it. */
export const SUB_PAY_ROLES = ["Admin"];
export const canSeeSubPay = (role) => SUB_PAY_ROLES.includes(role);
